import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Download, FileText, KeyRound, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { AdminTitle, Field } from "@/components/admin/admin-ui";
import { Button } from "@/components/ui/button";
import { meta } from "@/components/store/page-parts";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/inventory")({
  head: () => meta("Inventory — BetterAccess Admin", "Monitor, upload and export secure digital credentials and stock keys."),
  component: Inventory,
});

function Inventory() {
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [stockDetails, setStockDetails] = useState<{ available: any[]; used: any[] }>({ available: [], used: [] });
  const [loading, setLoading] = useState(true);
  const [stockText, setStockText] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchProducts = async () => {
    try {
      const res = await fetch("/api/products");
      if (res.ok) {
        const d = await res.json();
        if (Array.isArray(d.products) && d.products.length > 0) {
          setProducts(d.products);
          if (!selectedProductId) setSelectedProductId(d.products[0].id);
        }
      }
    } catch {
      // ignore
    }
  };

  const fetchStockDetails = async (prodId: string) => {
    if (!prodId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/stock/details/${encodeURIComponent(prodId)}`);
      if (res.ok) {
        const data = await res.json();
        setStockDetails({ available: data.available || [], used: data.used || [] });
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      void fetchStockDetails(selectedProductId);
    }
  }, [selectedProductId]);

  const handleBulkUpload = async () => {
    if (!selectedProductId || !stockText.trim()) {
      toast.error("Please enter credentials / keys to upload");
      return;
    }

    setUploading(true);
    try {
      const res = await fetch("/api/admin/stock/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId: selectedProductId, lines: stockText.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Upload failed");
      toast.success(`Successfully added ${data.addedCount || "stock"} items!`);
      setStockText("");
      await fetchStockDetails(selectedProductId);
    } catch (err: any) {
      toast.error(err.message || "Could not upload stock items");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      const res = await fetch(`/api/admin/stock/item/${encodeURIComponent(id)}?productId=${encodeURIComponent(selectedProductId)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete stock item");
      toast.success("Stock item removed");
      await fetchStockDetails(selectedProductId);
    } catch (err: any) {
      toast.error(err.message || "Error deleting item");
    }
  };

  const handleExportTxt = () => {
    if (!selectedProductId) return;
    window.open(`/api/admin/stock/export/${encodeURIComponent(selectedProductId)}`, "_blank");
  };

  const selectedProduct = products.find(p => p.id === selectedProductId);

  return (
    <>
      <AdminTitle
        eyebrow="Secure stock"
        title="Inventory Vault"
        copy="Manage digital credentials, license keys, and account logins with automated 1-click delivery."
        action={
          <Button variant="outline" onClick={handleExportTxt} disabled={!stockDetails.available.length}>
            <Download />
            Export TXT
          </Button>
        }
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <div className="premium-panel flex items-center gap-4 p-4">
          <span className="grid size-10 place-items-center rounded-md bg-emerald-500/10 text-emerald-500">
            <KeyRound className="size-5" />
          </span>
          <div>
            <b className="block text-xl">{stockDetails.available.length}</b>
            <small className="text-muted-foreground">Available in Database</small>
          </div>
        </div>

        <div className="premium-panel flex items-center gap-4 p-4">
          <span className="grid size-10 place-items-center rounded-md bg-blue-500/10 text-blue-500">
            <FileText className="size-5" />
          </span>
          <div>
            <b className="block text-xl">{stockDetails.used.length}</b>
            <small className="text-muted-foreground">Delivered to Customers</small>
          </div>
        </div>

        <div className="premium-panel flex items-center gap-4 p-4">
          <span className="grid size-10 place-items-center rounded-md bg-primary/10 text-primary">
            <Upload className="size-5" />
          </span>
          <div>
            <b className="block text-xl">{products.length}</b>
            <small className="text-muted-foreground">Configured Products</small>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        <div>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <label className="text-xs font-bold text-muted-foreground">SELECT PRODUCT:</label>
              <select
                value={selectedProductId}
                onChange={e => setSelectedProductId(e.target.value)}
                className="mt-1 h-11 w-full rounded-md border bg-background px-3 font-semibold outline-none focus:ring-2 focus:ring-primary"
              >
                {products.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.fulfillment_type || "digital"})
                  </option>
                ))}
              </select>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="mt-auto h-11"
              onClick={() => fetchStockDetails(selectedProductId)}
              disabled={loading}
            >
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border bg-card">
            <div className="border-b bg-secondary/60 px-4 py-3 text-xs font-bold">
              AVAILABLE STOCK ITEMS FOR {selectedProduct?.name?.toUpperCase() || "SELECTED PRODUCT"} ({stockDetails.available.length})
            </div>
            {stockDetails.available.length > 0 ? (
              <div className="divide-y max-h-96 overflow-y-auto font-mono text-xs">
                {stockDetails.available.map((item, index) => (
                  <div key={item.id} className="flex items-center justify-between p-3 transition hover:bg-secondary/40">
                    <span className="truncate pr-4 font-semibold text-foreground">
                      #{index + 1}: {item.content}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(item.id)}
                      className="shrink-0 text-destructive hover:text-destructive/80"
                      title="Delete stock item"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">
                <KeyRound className="mx-auto size-8 opacity-40" />
                <p className="mt-2 text-sm font-semibold">No available stock items in vault</p>
                <p className="mt-1 text-xs">Paste new credentials on the right to upload.</p>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Upload Form */}
        <div className="rounded-lg border bg-card p-5">
          <h2 className="text-base font-black">Bulk Stock Upload</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Paste credentials, license codes, or links (one item per line) to instantly stock this product.
          </p>

          <div className="mt-4">
            <label className="text-xs font-bold text-foreground">Paste Lines:</label>
            <textarea
              rows={8}
              value={stockText}
              onChange={e => setStockText(e.target.value)}
              placeholder="user1@example.com:password123&#10;user2@example.com:password456&#10;LICENSE-KEY-XXXX-YYYY"
              className="mt-1.5 w-full rounded-md border bg-background p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>Lines detected: {stockText.trim() ? stockText.trim().split("\n").filter(Boolean).length : 0}</span>
          </div>

          <Button className="mt-4 w-full font-bold" onClick={handleBulkUpload} disabled={uploading || !stockText.trim()}>
            {uploading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="size-4 animate-spin" /> Uploading to Database...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Plus className="size-4" /> Stock Product
              </span>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}